import React, { useCallback, useState } from 'react';

import { Button, Drawer, Tag, Typography } from 'antd';

import Truncate from 'components/Truncate';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ActionCollapse from 'v2/components/Shared/QuickView/ActionCollapse';
import { useUserStore } from 'v2/store/User';

import { Icon } from '@eduzz/ui-antd-theme';

import CampaignForm from '../CampaignForm';
import ClipboardField from '../ClipboardField';
import TrackerForm from '../TrackerForm';

interface IProps {
  active: boolean;
  product: any;
  campaign: any;
  changeActive: any;
  updateListCampaigns: any;
}

const QuickView: React.FC<IProps> = ({ active, product, campaign, changeActive, updateListCampaigns }) => {
  const [activeCampaignForm, setActiveCampaignForm] = useState(false);
  const [activeTrackerForm, setActiveTrackerForm] = useState(false);

  const { currentUserId } = useUserStore(({ user }) => ({ currentUserId: user.id }));

  const handleCloseQuickView = useCallback(() => {
    changeActive();
  }, [changeActive]);

  const openEditForm = useCallback(() => {
    setActiveCampaignForm(true);
  }, []);

  const closeEditForm = useCallback(
    (saved?: boolean) => {
      setActiveCampaignForm(false);

      if (saved === true) {
        handleCloseQuickView();
      }
    },
    [handleCloseQuickView]
  );

  const closeTrackerForm = useCallback(() => {
    setActiveTrackerForm(false);
  }, []);

  return (
    <>
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={active}
        width='min(450px, 100vw)'
        title={product?.title ?? 'Carregando...'}
      >
        <div className='flex w-full flex-col gap-4'>
          <div className='flex items-center justify-between gap-4'>
            <Typography.Text strong title={campaign.title ? campaign.title : 'Produto desconhecido'}>
              <Truncate count={40} final={'...'}>
                {campaign.title ? campaign.title : 'Produto desconhecido'}
              </Truncate>
            </Typography.Text>

            <PermissionHide action='myeduzz:links:editar'>
              <Button color='default' variant='filled' onClick={openEditForm} icon={<Icon.Pencil size={16} />}>
                Editar
              </Button>
            </PermissionHide>
          </div>

          <div>{campaign.active ? <Tag color='success'>Ativa</Tag> : <Tag color='error'>Inativa</Tag>}</div>

          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            <div className='flex justify-between gap-2'>
              <Typography.Text type='secondary'>RPC A</Typography.Text>
              <Typography.Text strong>{campaign.rpc_affiliate}</Typography.Text>
            </div>
            <div className='flex justify-between gap-2'>
              <Typography.Text type='secondary'>RPC P</Typography.Text>
              <Typography.Text strong>{campaign.rpc_producer}</Typography.Text>
            </div>
            <div className='flex justify-between gap-2'>
              <Typography.Text type='secondary'>Cliques</Typography.Text>
              <Typography.Text strong>{campaign.clicks}</Typography.Text>
            </div>
            <div className='flex justify-between gap-2'>
              <Typography.Text type='secondary'>Conversões</Typography.Text>
              <Typography.Text strong>{campaign.conversion}</Typography.Text>
            </div>
          </div>

          <ActionCollapse title='Script de marcação' icon={<Icon.Code size={16} />}>
            <ClipboardField
              value={`<script>
                    (function(i,s,o,g,c,f,r,a,m)
                    {i['edzpx_c']=c;i['edzpx_a']=f;i['PxEdz']=r;i[r]=i[r]||
                    function(){(i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();
                    a=s.createElement(o),m=s.getElementsByTagName(o)[0]||
                    s.getElementsByTagName('head')[0];
                    e='https://e';g=e+'duzz'+'.'+String.fromCharCode(105, 110, 102, 111)+'/'+g;a.async=1;
                    a.src=g;m.parentNode.insertBefore(a,m)})(window,document,'script','dev/js/px.js','${campaign.key}',${currentUserId},'edzpx');</script>`}
            />
            <Typography.Paragraph type='secondary'>
              Clique{' '}
              <Typography.Link
                href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807655830-como-marcar-tr%C3%A1fego-com-script'
                target='_blank'
                rel='noopener noreferrer'
              >
                aqui
              </Typography.Link>{' '}
              e veja instruções de como usar.
            </Typography.Paragraph>
          </ActionCollapse>

          <ActionCollapse title='Script de Funil Infinito' icon={<Icon.Filter size={16} />}>
            <ClipboardField
              value={`<script>
                    var funnel_key = '${campaign.key}';
                    var funnel_level = '0';
                    var field_email = 'email';
                    var edz_loadscript = document.createElement('script');
                    edz_loadscript.src = 'https://eduzz.com/dev/js/ea.js?v=1.0';
                    document.getElementsByTagName('head')[0].appendChild(edz_loadscript);
                    </script>`}
            />
            <Typography.Paragraph type='secondary'>
              Clique{' '}
              <Typography.Link
                href='https://ajuda.eduzz.com/hc/pt-br/articles/4402979200027-o-que-%C3%A9-um-funil-infinito-myeduzz'
                target='_blank'
                rel='noopener noreferrer'
              >
                aqui
              </Typography.Link>{' '}
              e veja instruções de como usar.
            </Typography.Paragraph>
          </ActionCollapse>
        </div>
      </Drawer>

      {activeCampaignForm && (
        <CampaignForm
          open={activeCampaignForm}
          handleClose={closeEditForm}
          product={product}
          campaign={campaign}
          updateListCampaigns={updateListCampaigns}
        />
      )}
      {activeTrackerForm && (
        <TrackerForm open={activeTrackerForm} handleClose={closeTrackerForm} product={product} campaign={campaign} />
      )}
    </>
  );
};

export default QuickView;
